// TagDropdownPlugin.tsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";

import { $getNodeByKey, $getRoot, $getSelection, $isRangeSelection, $isTextNode } from "lexical";

import { $createTagNode } from "./TagNode";
import { Tag } from "../../interfaces/Tag";
import { TruncatedText } from "../truncatedText/TruncatedText";
import { DirectionalHint } from "@fluentui/react";
import { PILL_BASE_STYLE } from "../TrackedChangeOperationTypePill";
import { formatMessage } from "../../../utils/helpers/LanguageHelper";

const styles = {
  addTagText: {
    fontSize: "11px",
    fontWeight: 600,
    color: "var(--color-blue)",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: "4px",
    userSelect: "none",
  } as React.CSSProperties,

  popoverSurface: {
    backgroundColor: "white",
    borderRadius: "4px",
    padding: ".5rem",
    boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
    maxHeight: "200px",
    overflowY: "auto",
    minWidth: "200px",
    maxWidth: "400px",
    zIndex: 99999,
  } as React.CSSProperties,

  optionRow: {
    display: "flex",
    alignItems: "center",
    cursor: "pointer",
    padding: "2px 0",
    fontSize: "13px",
    borderRadius: "2px",
  } as React.CSSProperties,

  pill: {
    ...PILL_BASE_STYLE,
  },
};

/**
 * Computes a viewport-safe popover position relative to the reference rect,
 * flipping/clamping as needed.
 */
function computePopoverPosition(refRect: DOMRect, popoverEl: HTMLElement, margin = 8) {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const popRect = popoverEl.getBoundingClientRect();

  // Prefer below, left-aligned
  let top = refRect.bottom + window.scrollY + 4;
  let left = refRect.left + window.scrollX;

  // If it overflows right, align right
  if (left + popRect.width > vw + window.scrollX - margin) {
    left = refRect.right + window.scrollX - popRect.width;
  }
  // Clamp horizontally
  left = Math.max(window.scrollX + margin, Math.min(left, vw + window.scrollX - popRect.width - margin));

  // If it overflows bottom, try above
  if (top + popRect.height > vh + window.scrollY - margin) {
    const aboveTop = refRect.top + window.scrollY - popRect.height - 4;
    if (aboveTop >= window.scrollY + margin) {
      top = aboveTop;
    } else {
      // Clamp vertically
      top = Math.max(window.scrollY + margin, vh + window.scrollY - popRect.height - margin);
    }
  }

  return { top, left };
}

/**
 * A robust selection/caret rect getter that falls back to client rects.
 */
const useGetCursorRect = () => {
  return useCallback((): DOMRect | null => {
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return null;

    const range = sel.getRangeAt(0);
    let rect = range.getBoundingClientRect();

    if ((!rect || (rect.width === 0 && rect.height === 0)) && range.getClientRects().length > 0) {
      rect = range.getClientRects()[0];
    }
    return rect ?? null;
  }, []);
};

export const TagDropdownPlugin = ({ tags, disabled }: { tags: Tag[]; disabled?: boolean }) => {
  const [editor] = useLexicalComposerContext();

  const [showDropdown, setShowDropdown] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(-1);

  // Ref speculari per evitare closure stale nel listener keydown del document.
  const activeIndexRef = useRef(-1);
  const filteredTagsRef = useRef<Tag[]>([]);

  const [popoverPosition, setPopoverPosition] = useState<{ top: number; left: number } | null>(null);

  const [triggeredByAt, setTriggeredByAt] = useState(false);
  const [atPosition, setAtPosition] = useState<{ nodeKey: string; offset: number } | null>(null);

  const btnRef = useRef<HTMLDivElement | null>(null);
  const popRef = useRef<HTMLDivElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const activeRef = useRef<HTMLDivElement | null>(null);

  // Reference rect (button or cursor)
  const refRectRef = useRef<DOMRect | null>(null);

  // When opening via '@', the immediate next update cycle can run before
  // state/positioning stabilizes and incorrectly trigger `close()`.
  // Use this guard to skip exactly one update cycle after opening.
  const ignoreNextUpdateRef = useRef<boolean>(false);

  const getCursorRect = useGetCursorRect();

  const filteredTags = useMemo(() => {
    const list = searchQuery ? tags.filter((tag) => tag.label.toLowerCase().includes(searchQuery.toLowerCase())) : tags;
    return [...list].sort((a, b) => a.label.localeCompare(b.label));
  }, [tags, searchQuery]);

  // Mantieni i ref allineati ai valori correnti per il listener keydown sul document.
  useEffect(() => {
    filteredTagsRef.current = filteredTags;
  }, [filteredTags]);
  useEffect(() => {
    activeIndexRef.current = activeIndex;
  }, [activeIndex]);

  const close = useCallback(() => {
    setShowDropdown(false);
    setSearchQuery("");
    setActiveIndex(-1);
    setTriggeredByAt(false);
    setAtPosition(null);
    setPopoverPosition(null);
    refRectRef.current = null;
  }, []);

  //
  // Insert a tag
  //
  const insertTag = useCallback(
    (tag: Tag) => {
      if (disabled) return;

      editor.update(() => {
        const selection = $getSelection();
        if ($isRangeSelection(selection)) {
          if (triggeredByAt && atPosition) {
            const node = $getNodeByKey(atPosition.nodeKey);
            if (node && $isTextNode(node)) {
              const text = node.getTextContent();
              const anchor = selection.anchor;
              const isSameNode = anchor.key === node.getKey();
              const endOffset = isSameNode ? anchor.offset : text.length;

              const before = text.slice(0, atPosition.offset);
              const after = text.slice(endOffset);
              node.setTextContent(before + after);

              selection.anchor.set(node.getKey(), atPosition.offset, "text");
              selection.focus.set(node.getKey(), atPosition.offset, "text");
            }
          }

          const hasObjects = !!(tag.objects && tag.objects.length > 0);
          const isDeleted = !!tag.isDeleted;
          const node = $createTagNode(tag.id, tag.label, hasObjects, isDeleted);
          selection.insertNodes([node]);
          selection.insertText(" ");
        }
      });

      close();
      setTimeout(() => editor.focus());
    },
    [editor, disabled, triggeredByAt, atPosition, close]
  );

  //
  // Handle '@' trigger
  //
  useEffect(() => {
    if (disabled) return;

    return editor.registerUpdateListener(({ editorState }) => {
      // Skip exactly one update cycle right after we opened from '@',
      // ma solo se la '@' è ancora presente nell'editor.
      // Se è già stata cancellata (Backspace/Delete rapido), chiudiamo subito.
      if (ignoreNextUpdateRef.current) {
        ignoreNextUpdateRef.current = false;
        const atExists = editorState.read(() => $getRoot().getTextContent().includes("@"));
        if (!atExists) {
          close();
          return;
        }
        return;
      }

      editorState.read(() => {
        const sel = $getSelection();
        if ($isRangeSelection(sel)) {
          const anchor = sel.anchor;
          const node = anchor.getNode();

          if ($isTextNode(node)) {
            const text = node.getTextContent();
            const offset = anchor.offset;

            // user just typed '@' — or cursor is right after an existing '@'
            if (offset > 0 && text[offset - 1] === "@") {
              if (triggeredByAt && showDropdown) {
                // La dropdown è già aperta: il cursore è tornato subito dopo la @
                // (es. l'utente ha cancellato il testo di filtro).
                // Aggiorna solo la searchQuery senza resettare la posizione.
                setSearchQuery("");
                // Aggiorna nodeKey se Lexical ha ricreato il nodo
                setAtPosition({ nodeKey: node.getKey(), offset: offset - 1 });
                return;
              }

              // Nuova @: apri la dropdown
              const rect = getCursorRect();
              if (rect) {
                refRectRef.current = rect;
              }
              if (refRectRef.current) {
                setAtPosition({
                  nodeKey: node.getKey(),
                  offset: offset - 1,
                });
                setShowDropdown(true);
                ignoreNextUpdateRef.current = true;
                setTriggeredByAt(true);
                setSearchQuery("");
                setActiveIndex(-1);
                setPopoverPosition(null);
              }
              return;
            }

            // update search if still inside @ context
            if (triggeredByAt && showDropdown) {
              const before = text.substring(0, offset);
              const atIdx = before.lastIndexOf("@");

              if (atIdx !== -1 && atPosition) {
                if (node.getKey() === atPosition.nodeKey) {
                  if (text[atPosition.offset] === "@") {
                    const q = before.substring(atIdx + 1);
                    setSearchQuery(q);
                    return;
                  }
                }
              }

              close();
            }
          } else if (triggeredByAt && showDropdown) {
            close();
          }
        } else if (triggeredByAt && showDropdown) {
          close();
        }
      });
    });
  }, [editor, disabled, triggeredByAt, showDropdown, atPosition, getCursorRect, close]);

  //
  // Toggle dropdown from button — inserisce "@" nell'editor per triggerare
  // il meccanismo esistente di apertura dropdown via registerUpdateListener.
  //
  const toggleFromButton = () => {
    if (disabled) return;

    if (showDropdown) {
      close();
      return;
    }

    editor.update(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        selection.insertText("@");
      }
    });
    setTimeout(() => editor.focus());
  };

  //
  // Compute/update popover position after it mounts and on scroll/resize
  //
  useEffect(() => {
    if (!showDropdown) return;

    const measureAndSet = () => {
      if (!refRectRef.current || !popRef.current) return;
      const pos = computePopoverPosition(refRectRef.current, popRef.current);
      setPopoverPosition(pos);
    };

    // Defer to next frame to ensure popover is in DOM & sized
    const raf = requestAnimationFrame(measureAndSet);

    const onReflow = () => {
      // Refresh reference rect
      if (triggeredByAt) {
        const currentCursorRect = getCursorRect();
        if (currentCursorRect) {
          refRectRef.current = currentCursorRect;
        }
      } else if (btnRef.current) {
        refRectRef.current = btnRef.current.getBoundingClientRect();
      }
      measureAndSet();
    };

    window.addEventListener("resize", onReflow);
    window.addEventListener("scroll", onReflow, true);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", onReflow);
      window.removeEventListener("scroll", onReflow, true);
    };
  }, [showDropdown, triggeredByAt, getCursorRect]);

  //
  // Keyboard navigation — ascolta direttamente sul document in capture
  // per intercettare i tasti prima di qualsiasi handler Lexical.
  //
  useEffect(() => {
    if (!showDropdown) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          e.stopPropagation();
          setActiveIndex((i) => (filteredTagsRef.current.length === 0 ? -1 : (i + 1) % filteredTagsRef.current.length));
          break;
        case "ArrowUp":
          e.preventDefault();
          e.stopPropagation();
          setActiveIndex((i) =>
            filteredTagsRef.current.length === 0 ? -1 : i <= 0 ? filteredTagsRef.current.length - 1 : i - 1
          );
          break;
        case "Enter":
        case "Tab":
          e.preventDefault();
          e.stopPropagation();
          {
            const idx = activeIndexRef.current;
            const tags = filteredTagsRef.current;
            if (idx >= 0 && idx < tags.length) {
              insertTag(tags[idx]);
            }
          }
          break;
        case "Escape":
          e.preventDefault();
          e.stopPropagation();
          close();
          break;
        default:
          break;
      }
    };

    document.addEventListener("keydown", handleKeyDown, true);
    return () => document.removeEventListener("keydown", handleKeyDown, true);
  }, [showDropdown, insertTag, close]);

  //
  // Keep active item visible while arrowing
  //
  useEffect(() => {
    if (activeRef.current && popRef.current) {
      const el = activeRef.current;
      const container = popRef.current;
      const elTop = el.offsetTop;
      const elBottom = elTop + el.offsetHeight;
      const viewTop = container.scrollTop;
      const viewBottom = viewTop + container.clientHeight;

      if (elTop < viewTop) {
        container.scrollTop = elTop;
      } else if (elBottom > viewBottom) {
        container.scrollTop = elBottom - container.clientHeight;
      }
    }
  }, [activeIndex]);

  //
  // Render
  //
  return (
    <div ref={containerRef} style={{ position: "relative" }}>
      <div
        ref={btnRef}
        role="button"
        style={styles.addTagText}
        onClick={toggleFromButton}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") toggleFromButton();
        }}
      >
        <span style={{ fontSize: "8px" }}>▼</span> {formatMessage("cof.validation.button.addtag")}
      </div>

      {showDropdown && (
        <div
          ref={popRef}
          data-tag-dropdown-popover="true"
          style={{
            ...styles.popoverSurface,
            position: "fixed",
            // Mount off-screen and hidden until positioned, then show.
            top: popoverPosition?.top ?? -99999,
            left: popoverPosition?.left ?? -99999,
            visibility: popoverPosition ? "visible" : "hidden",
          }}
        >
          {filteredTags.length === 0 && <div style={{ padding: 8, color: "#777" }}>Nessun tag trovato</div>}

          {filteredTags.map((tag, i) => {
            const valid = (tag.objects?.length ?? 0) > 0;
            const bg = valid ? "var(--color-blue)" : "var(--color-red)";

            return (
              <div
                key={tag.id}
                ref={i === activeIndex ? activeRef : null}
                role="option"
                aria-selected={i === activeIndex}
                tabIndex={-1}
                style={{
                  ...styles.optionRow,
                  backgroundColor: i === activeIndex ? "var(--color-grey)" : "transparent",
                }}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => insertTag(tag)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") insertTag(tag);
                }}
              >
                <span style={{ ...styles.pill, backgroundColor: bg }}>
                  <TruncatedText text={tag.label} tooltipDirectionalHint={DirectionalHint.bottomCenter} />
                  {!valid && (
                    <img
                      src="/assets/warningRhombus.svg"
                      alt="warning"
                      style={{ width: 10, height: 10, marginLeft: 5 }}
                    />
                  )}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
